import React, { useState, useRef, useCallback, useEffect } from 'react';
import { toastSuccess, toastError } from '../utils/toast';
import { format } from 'date-fns';
import ContextView from './ChatInterface/ContextView';
import { ReactComponent as BrainIcon } from '../assets/brain_icon.svg';
import { ReactComponent as ChatIcon } from '../assets/chat_icon.svg';
import { useAuth, useUser } from '@clerk/clerk-react';
import { getApiUrl } from '../utils/apiUtils';
import { debugLog, debugWarn, debugError } from '../utils/debug';

const ConversationSidebar = ({
  conversations,
  activeConversationId,
  setActiveConversationId,
  isOpen,
  setIsOpen,
  startNewChat,
  deleteConversation,
  isLoading,
  drawerRef,
  overlayRef
}) => {
  // Track previous conversation IDs to detect actual changes
  const prevConversationIdsRef = useRef(null);
  
  // 🎯 FIX: Only log when conversations actually change (not on every render)
  useEffect(() => {
    const currentIds = conversations?.map(c => c.id).join(',') || '';
    const prevIds = prevConversationIdsRef.current;
    
    // Only log if conversation IDs actually changed
    if (currentIds !== prevIds) {
      debugLog('📱 [SIDEBAR] Conversations changed', {
        count: conversations?.length || 0,
        conversationIds: conversations?.map(c => c.id) || [],
        activeConversationId,
        isOpen
      });
      prevConversationIdsRef.current = currentIds;
    }
  }, [conversations, activeConversationId, isOpen]);
  
  // Track window width for responsive behavior
  const [isDesktop, setIsDesktop] = useState(() => 
    typeof window !== 'undefined' && window.innerWidth >= 1024
  );
  
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const handleResize = () => {
      if (typeof window !== 'undefined') {
        setIsDesktop(window.innerWidth >= 1024);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, []);
  // Use ref to track if we've manually set the tab (prevents auto-reset)
  const tabManuallySetRef = useRef(false);
  // Ref to track content area scroll position
  const contentAreaRef = useRef(null);
  // Ref for sticky header to measure its height
  const stickyHeaderRef = useRef(null);
  const tabBarRef = useRef(null); // Ref for the tab bar specifically
  const conversationListRef = useRef(null);
  
  const [activeTab, setActiveTab] = useState(() => {
    // Preserve tab state from localStorage to prevent reset when conversations load
    const savedTab = localStorage.getItem('conversationSidebarActiveTab');
    const initialTab = savedTab === 'context' ? 'context' : 'conversations';
    if (savedTab === 'context') {
      tabManuallySetRef.current = true; // Mark as manually set if it was saved
    }
    return initialTab;
  });
  const [onboardingInfo, setOnboardingInfo] = useState(null);
  const [promptInfo, setPromptInfo] = useState(null);
  const [loadingOnboardingInfo, setLoadingOnboardingInfo] = useState(false);
  
  const { getToken } = useAuth();
  const { user } = useUser();

  // Wrapper for setActiveTab that marks tab as manually set
  const handleSetActiveTab = (newTab) => {
    // 🎯 FIX: Validate tab value before setting
    if (newTab !== 'conversations' && newTab !== 'context') {
      debugWarn('⚠️ [SIDEBAR] Invalid tab value:', newTab, '- ignoring');
      return;
    }
    
    tabManuallySetRef.current = true;
    setActiveTab(newTab);
    localStorage.setItem('conversationSidebarActiveTab', newTab);
  };

  // Persist activeTab to localStorage when it changes
  React.useEffect(() => {
    if (tabManuallySetRef.current) {
      localStorage.setItem('conversationSidebarActiveTab', activeTab);
    }
  }, [activeTab]);

  // No longer needed - MessageInput is now part of flex layout in ChatInterface, not fixed
  // Sidebar scrollable areas naturally respect their container boundaries via flexbox

  // Fetch onboarding and prompt information
  const fetchOnboardingInfo = useCallback(async () => {
    if (!user || loadingOnboardingInfo) return;
    
    setLoadingOnboardingInfo(true);
    // Clear previous state to force fresh fetch
    setOnboardingInfo(null);
    setPromptInfo(null);
    
    try {
      const API_URL = getApiUrl();
      const token = await getToken();
      
      debugLog('🔍 Fetching onboarding info...');
      
      // Fetch onboarding status
      const onboardingResponse = await fetch(`${API_URL}/user/onboarding-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (onboardingResponse.ok) {
        const onboardingData = await onboardingResponse.json();
        debugLog('✅ Onboarding data:', onboardingData);
        setOnboardingInfo(onboardingData);
      } else {
        debugError('❌ Failed to fetch onboarding status:', onboardingResponse.status);
      }
      
      // Determine the active prompt set for the current conversation
      let activePromptSet = null;
      let promptSetId = null;
      
      if (activeConversationId && conversations.length > 0) {
        // Find the current conversation and its active prompt set
        const currentConversation = conversations.find(c => c.id === activeConversationId);
        
        if (currentConversation) {
          // 🎯 FIX: Always prioritize active_prompt_set_id over the relationship object
          // The relationship object might be stale or incomplete (only has id, name, description)
          // The active_prompt_set_id is the source of truth
          if (currentConversation.active_prompt_set_id) {
            promptSetId = currentConversation.active_prompt_set_id;
          } else if (currentConversation.active_prompt_set) {
            // Fallback: if no active_prompt_set_id but relationship exists, use it
            activePromptSet = currentConversation.active_prompt_set;
            promptSetId = currentConversation.active_prompt_set.id;
          }
        }
      }
      
      // If we have a promptSetId but no activePromptSet object, fetch it
      if (promptSetId && !activePromptSet) {
        debugLog('🔍 Fetching prompt set details for ID:', promptSetId);
        try {
          const promptSetResponse = await fetch(`${API_URL}/api/prompt-sets/${promptSetId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (promptSetResponse.ok) {
            activePromptSet = await promptSetResponse.json();
            debugLog('✅ Fetched conversation-specific prompt set:', activePromptSet.name);
          } else {
            debugWarn('⚠️ Failed to fetch prompt set', promptSetId, '- will fall back to default');
            promptSetId = null; // Clear so we fall back to default
          }
        } catch (error) {
          debugError('❌ Error fetching prompt set details:', error);
          promptSetId = null; // Clear so we fall back to default
        }
      }
      
      // If no conversation-specific prompt set, fetch all prompt sets and find default
      if (!activePromptSet) {
        debugLog('🔍 Fetching prompt sets...');
        const promptSetsResponse = await fetch(`${API_URL}/api/prompt-sets`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (promptSetsResponse.ok) {
          const promptSetsData = await promptSetsResponse.json();
          debugLog('✅ Prompt sets data:', promptSetsData);
          
          // Find the default prompt set
          activePromptSet = promptSetsData.find(ps => ps.is_default);
          
          // Fallback: if no default found, use the first active prompt set
          if (!activePromptSet && promptSetsData.length > 0) {
            activePromptSet = promptSetsData.find(ps => ps.is_active) || promptSetsData[0];
            debugLog('⚠️ No default prompt set found, using fallback:', activePromptSet?.name);
          }
          
          if (activePromptSet) {
            promptSetId = activePromptSet.id;
          }
          
          debugLog('🎯 Default prompt set:', activePromptSet);
        } else {
          debugError('❌ Failed to fetch prompt sets:', promptSetsResponse.status);
          const errorText = await promptSetsResponse.text();
          debugError('❌ Prompt sets error response:', errorText);
        }
      }
      
      // Fetch full prompt set details including basics_prompt (if we don't already have them)
      if (promptSetId && (!activePromptSet || !activePromptSet.basics_prompt)) {
        // Removed verbose logging - prompt set data is too large for console
        try {
          const fullPromptSetResponse = await fetch(`${API_URL}/api/prompt-sets/${promptSetId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (fullPromptSetResponse.ok) {
            const fullPromptSetData = await fullPromptSetResponse.json();
            // Removed: debugLog('✅ Full prompt set data:', fullPromptSetData); // Too verbose - contains entire prompt text
            
            // Merge the full data with the basic data
            activePromptSet = {
              ...activePromptSet,
              ...fullPromptSetData
            };
          } else {
            debugError('❌ Failed to fetch full prompt set details:', fullPromptSetResponse.status);
          }
        } catch (error) {
          debugError('❌ Error fetching full prompt set details:', error);
        }
      }
      
      setPromptInfo({
        defaultPromptSet: activePromptSet,
        allPromptSets: [] // Not needed for this display
      });
    } catch (error) {
      debugError('❌ Error fetching onboarding info:', error);
    } finally {
      setLoadingOnboardingInfo(false);
    }
  }, [user, activeConversationId, conversations, loadingOnboardingInfo, getToken]);

  // Track the current conversation's active_prompt_set_id to detect changes
  // Use JSON.stringify to detect deep changes in the conversations array
  const conversationsKey = React.useMemo(() => {
    if (activeConversationId && conversations.length > 0) {
      const currentConversation = conversations.find(c => c.id === activeConversationId);
      // Create a key that changes when active_prompt_set_id changes
      return `${activeConversationId}-${currentConversation?.active_prompt_set_id || 'null'}`;
    }
    return null;
  }, [activeConversationId, conversations]);
  
  const currentPromptSetId = React.useMemo(() => {
    if (activeConversationId && conversations.length > 0) {
      const currentConversation = conversations.find(c => c.id === activeConversationId);
      const promptSetId = currentConversation?.active_prompt_set_id || null;
      return promptSetId;
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConversationId, conversationsKey, conversations]);

  // Use a ref to track previous promptSetId to detect changes
  const prevPromptSetIdRef = React.useRef(currentPromptSetId);
  // Track the last fetched combination to prevent infinite loops
  const lastFetchedRef = React.useRef({ promptSetId: null, activeTab: null, activeConversationId: null });

  // Fetch info when Memory tab is first opened or when conversation changes
  React.useEffect(() => {
    // Check if promptSetId actually changed
    const promptSetIdChanged = prevPromptSetIdRef.current !== currentPromptSetId;
    if (promptSetIdChanged) {
      debugLog('🔄 [SIDEBAR] PromptSetId changed from', prevPromptSetIdRef.current, 'to', currentPromptSetId);
      prevPromptSetIdRef.current = currentPromptSetId;
    }

    // Reset tracking when switching away from Memory tab
    if (activeTab !== 'context') {
      lastFetchedRef.current = { promptSetId: null, activeTab: null, activeConversationId: null };
      return;
    }

    // 🎯 FIX: Only fetch when Memory tab is active AND we have conversations loaded
    // Wait for conversations to load before fetching to avoid using default prompt set
    if (activeTab === 'context' && !loadingOnboardingInfo && user && conversations.length > 0 && activeConversationId) {
      // Check if we've already fetched for this exact combination
      const lastFetched = lastFetchedRef.current;
      const shouldFetch = 
        lastFetched.promptSetId !== currentPromptSetId ||
        lastFetched.activeTab !== activeTab ||
        lastFetched.activeConversationId !== activeConversationId;
      
      if (shouldFetch) {
        // Update the ref BEFORE fetching to prevent race conditions
        lastFetchedRef.current = {
          promptSetId: currentPromptSetId,
          activeTab: activeTab,
          activeConversationId: activeConversationId
        };
        fetchOnboardingInfo();
      }
    } else if (activeTab === 'context' && !loadingOnboardingInfo && user && conversations.length === 0) {
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, user, activeConversationId, conversations.length, currentPromptSetId, loadingOnboardingInfo]); // fetchOnboardingInfo intentionally omitted to prevent infinite loop - we track what we've fetched with lastFetchedRef

  // Listen for conversation refresh events (e.g., when coach is switched)
  React.useEffect(() => {
    const handleRefreshConversations = () => {
      // Wait a bit for conversations to refresh, then update prompt info
      // If Memory tab is active, refresh the prompt info to show updated coach
      // Use multiple timeouts to ensure conversations have updated
      setTimeout(() => {
        if (activeTab === 'context' && !loadingOnboardingInfo && user) {
          fetchOnboardingInfo();
        }
      }, 300); // First attempt after 300ms
      
      setTimeout(() => {
        if (activeTab === 'context' && !loadingOnboardingInfo && user) {
          fetchOnboardingInfo();
        }
      }, 1000); // Second attempt after 1s to catch delayed updates
    };

    window.addEventListener('runium:refresh-conversations', handleRefreshConversations);
    
    return () => {
      window.removeEventListener('runium:refresh-conversations', handleRefreshConversations);
    };
  }, [activeTab, loadingOnboardingInfo, user, fetchOnboardingInfo]); // Re-bind listener when these change


  // Prevent activeTab from being reset when conversations load
  // This ensures the Memory tab stays visible even when conversations finish loading
  React.useEffect(() => {
    // 🎯 FIX: Always ensure activeTab is valid ('conversations' or 'context')
    // This prevents invalid states that could cause rendering issues
    if (activeTab !== 'conversations' && activeTab !== 'context') {
      debugWarn('⚠️ [SIDEBAR] Invalid activeTab detected:', activeTab, '- resetting to conversations');
      tabManuallySetRef.current = true;
      setActiveTab('conversations');
      localStorage.setItem('conversationSidebarActiveTab', 'conversations');
      return;
    }
    
    // 🎯 FIX: If tab was manually set, don't try to restore - user's choice takes precedence
    if (tabManuallySetRef.current) {
      // Just verify localStorage matches current state (for consistency)
      const savedTab = localStorage.getItem('conversationSidebarActiveTab');
      if (savedTab && savedTab !== activeTab && (savedTab === 'context' || savedTab === 'conversations')) {
        localStorage.setItem('conversationSidebarActiveTab', activeTab);
      }
      return; // Don't restore if manually set
    }
    
    // Only restore from localStorage if tab wasn't manually set
    const savedTab = localStorage.getItem('conversationSidebarActiveTab');
    
    // Only restore if savedTab is valid and differs from current
    if (savedTab === 'context' && activeTab !== 'context') {
      tabManuallySetRef.current = true; // Mark as set to prevent further resets
      setActiveTab('context');
    } else if (savedTab === 'conversations' && activeTab !== 'conversations') {
      tabManuallySetRef.current = true; // Mark as set to prevent further resets
      setActiveTab('conversations');
    }
  }, [conversations.length, activeTab]); // Include activeTab to satisfy exhaustive-deps

  // Get an abbreviated title from the last message
  const getConversationTitle = (conversation) => {
    if (!conversation.lastMessage) return 'New Conversation';
    
    // Get the first sentence or first 30 characters
    const content = conversation.lastMessage.content || '';
    const firstSentence = content.split(/[.!?]/).filter(Boolean)[0];
    
    if (firstSentence) {
      return firstSentence.length > 30 
        ? firstSentence.substring(0, 30) + '...'
        : firstSentence;
    }
    
    return content.length > 30 
      ? content.substring(0, 30) + '...' 
      : content || 'New Conversation';
  };

  // Format date to display in sidebar
  const formatDate = (dateString) => {
    // Ensure timestamp is treated as UTC by adding 'Z' suffix if not present
    const utcTimestamp = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    const date = new Date(utcTimestamp);
    const now = new Date();
    
    // If it's today, show the time
    if (date.toDateString() === now.toDateString()) {
      return format(date, 'h:mm a');
    }
    
    // If it's this year, show month and day
    if (date.getFullYear() === now.getFullYear()) {
      return format(date, 'MMM d');
    }
    
    // Otherwise show month, day and year
    return format(date, 'MMM d, yyyy');
  };

  const renderOnboardingStatus = () => {
    if (loadingOnboardingInfo) {
      return (
        <div className="bg-gray-800 rounded-lg p-4 mb-4 border border-gray-700">
          <div className="flex items-center gap-2">
            <div className="animate-spin w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full"></div>
            <span className="text-gray-300 text-sm">Loading onboarding info...</span>
          </div>
        </div>
      );
    }

    if (!onboardingInfo) return null;

    const isOnboardingActive = !onboardingInfo.onboard_complete;

    return (
      <div className="bg-gray-800 rounded-lg p-3 sm:p-4 mb-4 border border-gray-700">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-white font-medium flex items-center gap-2 text-sm sm:text-base">
            🎯 Onboarding Status
          </h4>
          <button
            onClick={fetchOnboardingInfo}
            className="text-gray-400 hover:text-white text-xs sm:text-sm px-2 py-1 sm:px-3 sm:py-2 rounded border border-gray-600 hover:border-gray-500"
          >
            Refresh
          </button>
        </div>
        
        <div className="space-y-2 sm:space-y-3">
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${isOnboardingActive ? 'bg-orange-500' : 'bg-green-500'}`}></div>
            <span className="text-gray-300 text-sm sm:text-base">
              {isOnboardingActive ? 'Onboarding Active' : 'Onboarding Complete'}
            </span>
          </div>
          
          <div className="text-xs sm:text-sm text-gray-400 bg-gray-700 p-2 sm:p-3 rounded">
            <div className="font-medium mb-1">System Prompt Impact:</div>
            {isOnboardingActive ? (
              <div className="text-orange-300">
                ✓ AI will follow onboarding flow (questions, schedule creation, etc.)<br/>
                ✓ Weekly regimen tools are included in onboarding context
              </div>
            ) : (
              <div className="text-green-300">
                ✓ AI uses post-onboarding behavior<br/>
                ✓ Full tool access available
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };


  const renderPromptInfo = () => {
    if (!promptInfo?.defaultPromptSet) return null;

    const { defaultPromptSet } = promptInfo;

    return (
      <div className="bg-gray-800 rounded-lg p-3 sm:p-4 mb-4 border border-gray-700">
        <h4 className="text-white font-medium mb-3 flex items-center gap-2 text-sm sm:text-base">
          📝 Active Prompt Set
        </h4>
        
        <div className="space-y-3 sm:space-y-4">
          {/* Enhanced prompt set header with ID and status */}
          <div>
            <div className="text-sm sm:text-base text-gray-300 font-medium">
              {defaultPromptSet.name} (ID: {defaultPromptSet.id})
            </div>
            {defaultPromptSet.description && (
              <div className="text-xs sm:text-sm text-gray-400 mt-1">{defaultPromptSet.description}</div>
            )}
            
            {/* Status indicators */}
            <div className="flex flex-wrap gap-2 mt-2">
              <span className={`text-xs px-2 py-1 rounded ${
                defaultPromptSet.is_active 
                  ? 'bg-green-900 text-green-300' 
                  : 'bg-red-900 text-red-300'
              }`}>
                {defaultPromptSet.is_active ? '✅ Active' : '❌ Inactive'}
              </span>
              <span className={`text-xs px-2 py-1 rounded ${
                defaultPromptSet.is_default 
                  ? 'bg-blue-900 text-blue-300' 
                  : 'bg-gray-700 text-gray-400'
              }`}>
                {defaultPromptSet.is_default ? '⭐ Default' : '📝 Custom'}
              </span>
              {defaultPromptSet.is_system_fallback && (
                <span className="text-xs px-2 py-1 rounded bg-purple-900 text-purple-300">
                  🔧 System Fallback
                </span>
              )}
            </div>
          </div>
          
          {/* Negotiable content status */}
          <div className="bg-gray-700 p-2 rounded">
            <div className="text-xs text-gray-400 mb-1">🧩 Negotiable Content Status:</div>
            <div className="flex flex-wrap gap-1 text-xs">
              <span className={`px-1 py-0.5 rounded ${defaultPromptSet.personality_traits ? 'bg-green-800 text-green-300' : 'bg-gray-600 text-gray-500'}`}>
                Traits
              </span>
              <span className={`px-1 py-0.5 rounded ${defaultPromptSet.identity_guidelines ? 'bg-green-800 text-green-300' : 'bg-gray-600 text-gray-500'}`}>
                Guidelines
              </span>
              <span className={`px-1 py-0.5 rounded ${defaultPromptSet.communication_style ? 'bg-green-800 text-green-300' : 'bg-gray-600 text-gray-500'}`}>
                Style
              </span>
              <span className={`px-1 py-0.5 rounded ${defaultPromptSet.training_methods ? 'bg-green-800 text-green-300' : 'bg-gray-600 text-gray-500'}`}>
                Methods
              </span>
              <span className={`px-1 py-0.5 rounded ${defaultPromptSet.cultural_context ? 'bg-green-800 text-green-300' : 'bg-gray-600 text-gray-500'}`}>
                Context
              </span>
              <span className={`px-1 py-0.5 rounded ${defaultPromptSet.coaching_philosophy ? 'bg-green-800 text-green-300' : 'bg-gray-600 text-gray-500'}`}>
                Philosophy
              </span>
            </div>
          </div>
          
          <div className="space-y-2 sm:space-y-3">
            <div>
              <div className="text-xs sm:text-sm text-gray-400 mb-1">Basics Prompt Preview:</div>
              <div className="text-xs sm:text-sm text-gray-300 bg-gray-700 p-2 sm:p-3 rounded max-h-20 sm:max-h-24 overflow-y-auto">
                {defaultPromptSet.basics_prompt && defaultPromptSet.basics_prompt.trim() ? (
                  <>
                    {defaultPromptSet.basics_prompt.substring(0, 200)}
                    {defaultPromptSet.basics_prompt.length > 200 && '...'}
                  </>
                ) : (
                  // Fall back to showing negotiable content preview if basics_prompt is empty
                  (() => {
                    const negotiableContent = [
                      defaultPromptSet.identity_guidelines,
                      defaultPromptSet.personality_traits,
                      defaultPromptSet.communication_style,
                      defaultPromptSet.training_methods,
                      defaultPromptSet.coaching_philosophy,
                      defaultPromptSet.cultural_context
                    ].filter(Boolean).join(' ').trim();
                    
                    if (negotiableContent) {
                      return (
                        <>
                          {negotiableContent.substring(0, 200)}
                          {negotiableContent.length > 200 && '...'}
                        </>
                      );
                    }
                    // If no negotiable content, show that non-negotiable content is always present
                    return <span className="text-gray-500 italic">Using default coach identity (non-negotiable content always present)</span>;
                  })()
                )}
              </div>
            </div>
            
          </div>

          {/* Enhanced footer with more metadata */}
          <div className="text-xs sm:text-sm text-gray-400 pt-2 border-t border-gray-600">
            <div className="space-y-1">
              <div>
                Created by: {defaultPromptSet.created_by_name} (ID: {defaultPromptSet.created_by_user_id}) • 
                Usage: {defaultPromptSet.usage_count} times
              </div>
              <div className="text-xs text-gray-500">
                Created: {new Date(defaultPromptSet.created_at).toLocaleDateString()} • 
                Updated: {new Date(defaultPromptSet.updated_at).toLocaleDateString()}
              </div>
              <div className="text-xs text-gray-500">
                Prompt size: {defaultPromptSet.basics_prompt ? Math.round(defaultPromptSet.basics_prompt.length / 4) : 0} tokens
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDebugInfo = () => {
    return (
      <div className="bg-gray-800 rounded-lg p-4 mb-4 border border-gray-700">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-white font-medium flex items-center gap-2">
            🔧 Debug Information
          </h4>
          <div className="flex gap-2">
            <button
              onClick={() => {
                // Force refresh all data
                fetchOnboardingInfo();
              }}
              className="text-gray-400 hover:text-white text-xs px-2 py-1 rounded border border-gray-600 hover:border-gray-500"
            >
              Force Refresh
            </button>
            <button
              onClick={async () => {
                if (!user) return;
                try {
                  const API_URL = getApiUrl();
                  const token = await getToken();
                  
                  const response = await fetch(`${API_URL}/user/reset-onboarding-only`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` }
                  });
                  
                  const data = await response.json();
                  debugLog('🔧 Debug reset response:', data);
                  
                  if (data.success) {
                    // Refresh the onboarding info to see the change
                    setTimeout(() => {
                      fetchOnboardingInfo();
                    }, 500);
                    toastSuccess('Onboarding reset.');
                  } else {
                    toastError(`Reset failed: ${data.message}`);
                  }
                } catch (error) {
                  debugError('Debug reset error:', error);
                  toastError('Reset failed.');
                }
              }}
              className="text-orange-400 hover:text-orange-300 text-xs px-2 py-1 rounded border border-orange-600 hover:border-orange-500"
            >
              Reset Onboarding
            </button>
          </div>
        </div>
        
        <div className="space-y-2 text-xs">
          <div className="bg-gray-700 p-2 rounded">
            <div className="text-gray-300 font-medium mb-1">Current State:</div>
            <div className="space-y-1 text-gray-400">
              <div className="flex items-center gap-2">
                <span className="text-gray-400">•</span>
                <span>Loading: {loadingOnboardingInfo ? 'true' : 'false'}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-400">•</span>
                <span>Onboarding: {onboardingInfo?.onboard_complete ? 'Complete' : onboardingInfo ? 'Incomplete' : 'Unknown'}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-400">•</span>
                <span>Active Prompt: {promptInfo?.defaultPromptSet ? `${promptInfo.defaultPromptSet.name} (ID: ${promptInfo.defaultPromptSet.id})` : 'None'}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-400">•</span>
                <span>UI Sections: {onboardingInfo && promptInfo?.defaultPromptSet ? 'Both rendering' : 'Some missing'}</span>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-700 p-2 rounded">
            <div className="text-gray-300 font-medium mb-1">Raw API Data:</div>
            <div className="space-y-1 text-gray-400">
              <div className="text-xs">
                <span className="text-gray-500">onboardingInfo:</span> {onboardingInfo ? JSON.stringify(onboardingInfo) : 'null'}
              </div>
              <div className="text-xs">
                <span className="text-gray-500">promptSetId:</span> {promptInfo?.defaultPromptSet?.id || 'null'}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderFallbackChainStatus = () => {
    if (!promptInfo?.defaultPromptSet) return null;

    const { defaultPromptSet } = promptInfo;
    
    return (
      <div className="bg-gray-800 rounded-lg p-3 sm:p-4 mb-4 border border-gray-700">
        <h4 className="text-white font-medium mb-3 flex items-center gap-2 text-sm sm:text-base">
          🔗 Prompt Fallback Chain Status
        </h4>
        
        <div className="space-y-2 text-xs">
          <div className="bg-gray-700 p-2 rounded">
            <div className="text-gray-300 font-medium mb-1">Fallback Chain:</div>
            <div className="space-y-1 text-gray-400">
              <div className="flex items-center gap-2">
                <span className="text-green-400">✅</span>
                <span>User-specified prompt set (ID: {defaultPromptSet.id})</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-400">✅</span>
                <span>Database prompt validation passed</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-400">✅</span>
                <span>Content length: {defaultPromptSet.basics_prompt?.length || 0} chars (valid)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">❌</span>
                <span>System fallback: Not needed</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">❌</span>
                <span>Hardcoded fallback: Not needed</span>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-700 p-2 rounded">
            <div className="text-gray-300 font-medium mb-1">Content Validation:</div>
            <div className="space-y-1 text-gray-400">
              {(() => {
                // Combine all content fields (basics_prompt + negotiable fields) for validation
                const combinedContent = [
                  defaultPromptSet.basics_prompt,
                  defaultPromptSet.identity_guidelines,
                  defaultPromptSet.personality_traits,
                  defaultPromptSet.communication_style,
                  defaultPromptSet.training_methods,
                  defaultPromptSet.coaching_philosophy,
                  defaultPromptSet.cultural_context
                ].filter(Boolean).join(' ').trim();
                
                const contentLength = combinedContent.length;
                const hasCoachIdentity = combinedContent.toLowerCase().includes('coach');
                const hasRunningContext = combinedContent.toLowerCase().includes('running');
                
                return (
                  <>
                    <div className="flex items-center gap-2">
                      <span className={contentLength >= 50 ? 'text-green-400' : 'text-red-400'}>
                        {contentLength >= 50 ? '✅' : '❌'}
                      </span>
                      <span>Minimum length: {contentLength}/50 chars</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={hasCoachIdentity ? 'text-green-400' : 'text-red-400'}>
                        {hasCoachIdentity ? '✅' : '❌'}
                      </span>
                      <span>Contains coach identity</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={hasRunningContext ? 'text-green-400' : 'text-red-400'}>
                        {hasRunningContext ? '✅' : '❌'}
                      </span>
                      <span>Contains running context</span>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Overlay — always mounted on mobile, hook manages opacity/pointerEvents */}
      <div
        ref={overlayRef}
        className="fixed inset-0 bg-black z-20 lg:hidden"
        style={{ opacity: 0, pointerEvents: 'none' }}
        onClick={() => setIsOpen()}
      />
      
      {/* Sidebar — desktop: static grid column; mobile: fixed drawer managed by hook */}
      {isDesktop && !isOpen ? null : (
        <div
          ref={isDesktop ? undefined : drawerRef}
          className="sidebar w-80 bg-gray-900 border-r border-gray-700 z-30"
          style={isDesktop ? {
            display: 'grid',
            gridTemplateRows: 'auto auto 1fr',
            pointerEvents: 'auto',
            overflow: 'hidden',
            height: '100%'
          } : {
            position: 'fixed',
            top: 0,
            left: 0,
            bottom: 0,
            display: 'grid',
            gridTemplateRows: 'auto auto 1fr',
            transform: 'translateX(-320px)',
            willChange: 'transform',
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
            overflow: 'hidden'
          }}
        >
        {/* Sidebar header - Grid Row 1 (auto) */}
        <div ref={stickyHeaderRef} style={{ gridRow: '1', overflow: 'hidden' }} className="bg-gray-900">
          <div className="bg-gray-800 border-b border-gray-700 p-3 sm:p-4 flex justify-between items-center shadow-sm" style={{ minHeight: '56px', maxHeight: '56px' }}>
            <h2 className="text-gray-100 font-medium text-base sm:text-lg">
              {activeTab === 'conversations' ? 'Conversations' : 'AI Memory'}
            </h2>
            <div className="flex gap-2">
              {activeTab === 'conversations' && (
                <button
                  onClick={startNewChat}
                  className="flex items-center justify-center text-sm bg-gray-700 text-blue-400 hover:bg-gray-600 w-8 h-8 rounded-full transition-all duration-200 ease-in-out active:scale-95"
                  disabled={isLoading}
                  title="New Chat"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen();
                }}
                className="flex items-center justify-center text-sm bg-gray-700 text-gray-400 hover:bg-gray-600 w-8 h-8 rounded-full transition-all duration-200 ease-in-out active:scale-95"
                title="Close Sidebar"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Tab Bar - Grid Row 2 (auto) */}
          <div ref={tabBarRef} style={{ gridRow: '2', overflow: 'hidden' }} className="bg-gray-800/50 border-b border-gray-700 flex px-3 sm:px-4">
            <button
              onClick={() => handleSetActiveTab('conversations')}
              className={`flex-1 py-2 text-sm sm:text-base font-medium transition-colors ${
                activeTab === 'conversations'
                  ? 'text-blue-400 border-b-2 border-blue-400 bg-gray-800/70'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800/30'
              }`}
            >
              <div className="flex items-center justify-center gap-1 sm:gap-2">
                <ChatIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">Chats</span>
                <span className="sm:hidden">Chat</span>
              </div>
            </button>
            <button
              onClick={() => handleSetActiveTab('context')}
              className={`flex-1 py-2 text-sm sm:text-base font-medium transition-colors ${
                activeTab === 'context'
                  ? 'text-blue-400 border-b-2 border-blue-400 bg-gray-800/70'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800/30'
              }`}
            >
              <div className="flex items-center justify-center gap-1 sm:gap-2">
                <BrainIcon className="w-6 h-6 sm:w-8 sm:h-8" />
                <span className="hidden sm:inline">Memory</span>
                <span className="sm:hidden">AI</span>
              </div>
            </button>
          </div>
        </div>

        {/* Content Area - Grid Row 3 (1fr, scrollable) */}
        <div style={{ gridRow: '3', overflow: 'hidden', minHeight: 0 }}>
          {activeTab === 'conversations' ? (
            /* Conversation list */
            <div ref={conversationListRef} className="overflow-y-auto scrollbar-thin px-2 sm:px-3 pt-1 pb-2 sm:pb-3 space-y-2 sm:space-y-3" style={{
              height: '100%',
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(107, 114, 128, 0.6) rgba(31, 41, 55, 0.5)',
              WebkitOverflowScrolling: 'touch',
              overflowY: 'auto'
            }}>
              {conversations.length === 0 ? (
                <div className="text-center p-4 sm:p-6 text-gray-400 text-sm sm:text-base">
                  {isLoading ? 'Loading conversations...' : 'No conversations yet'}
                </div>
              ) : (
                conversations.map((conversation) => {
                  return (
                  <div
                    key={conversation.id}
                    className={`flex flex-col p-3 sm:p-4 rounded-lg cursor-pointer transition-all duration-200 ${
                      activeConversationId === conversation.id
                        ? 'bg-gray-800 border border-gray-700'
                        : 'hover:bg-gray-800/60'
                    }`}
                    onClick={() => {
                      setActiveConversationId(conversation.id);
                      setIsOpen(); // Close sidebar on mobile after selection
                    }}
                  >
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex-1">
                        <p className="text-gray-100 text-sm sm:text-base font-medium line-clamp-2">
                          {getConversationTitle(conversation)}
                        </p>
                        {conversation.message_count && (
                          <span className="text-xs text-gray-500 mt-1">
                            {conversation.message_count} message{conversation.message_count !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      <span className="text-xs sm:text-sm text-gray-400 whitespace-nowrap">
                        {conversation.lastMessage && formatDate(conversation.lastMessage.created_at)}
                      </span>
                    </div>
                    
                    {conversation.lastMessage && (
                      <p className="text-gray-400 text-xs sm:text-sm mt-2 line-clamp-2">
                        {conversation.lastMessage.content}
                      </p>
                    )}
                    
                    <div className="flex justify-end mt-2 sm:mt-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteConversation(conversation.id);
                        }}
                        className="text-xs sm:text-sm text-gray-400 hover:text-red-400 p-1 sm:p-2 rounded"
                        disabled={isLoading}
                        aria-label="Delete conversation"
                        title="Delete conversation"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  );
                })
              )}
            </div>
          ) : (
            /* Context View */
            <div 
              ref={contentAreaRef}
              className="overflow-y-auto scrollbar-thin p-3 sm:p-4 space-y-4 sm:space-y-6" 
              style={{
                height: '100%',
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(107, 114, 128, 0.6) rgba(31, 41, 55, 0.5)',
                WebkitOverflowScrolling: 'touch',
                overflowY: 'auto',
                // Force scrollbar visibility on Chrome mobile
                WebkitScrollbarWidth: 'thin'
              }}
            >
              {renderOnboardingStatus()}
              {renderPromptInfo()}
              {renderFallbackChainStatus()}
              {renderDebugInfo()}
              <ContextView activeConversationId={activeConversationId} />
            </div>
          )}
        </div>
        </div>
      )}
    </>
  );
};

export default ConversationSidebar; 